import {
  AlertTriangle,
  CalendarX2,
  Check,
  Loader2,
  MapPin,
  XCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import type { OrgTimeEntryIssue } from "../hooks/useTimeEntryIssues";
import {
  formatDateInTimezone,
  formatTimeInTimezone,
} from "../../utils/time-entry-schema";

interface TimeEntryIssuesTableProps {
  issues: OrgTimeEntryIssue[];
  canResolve?: boolean;
  resolvingShiftId?: string;
  onResolve: (shiftId: string) => void;
}

function getIssueLabel(issue: OrgTimeEntryIssue) {
  if (issue.issueKind === "rejected_time_entry") {
    return "Timesheet rejected";
  }

  switch (issue.issueType) {
    case "unable_to_attend":
      return "Provider couldn’t attend";
    case "incorrect_date_or_time":
      return "Date or time reported incorrect";
    default:
      return "Provider reported an error";
  }
}

export function TimeEntryIssuesTable({
  issues,
  canResolve = false,
  resolvingShiftId,
  onResolve,
}: TimeEntryIssuesTableProps) {
  return (
    <div className="space-y-3">
      {issues.map((issue) => {
        const isRejected = issue.issueKind === "rejected_time_entry";
        const Icon = isRejected
          ? XCircle
          : issue.issueType === "incorrect_date_or_time"
            ? CalendarX2
            : AlertTriangle;
        const providerName =
          [issue.providerFirstName, issue.providerLastName]
            .filter(Boolean)
            .join(" ") || "Unknown Provider";

        return (
          <Card
            key={`${issue.issueKind}-${issue.issueId}`}
            className="border-amber-200 bg-amber-50/30"
          >
            <CardContent className="p-4">
              <div className="flex flex-col justify-between gap-3 sm:flex-row">
                <div className="min-w-0 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      variant="outline"
                      className={
                        isRejected
                          ? "border-red-300 bg-red-100 text-red-700"
                          : "border-amber-300 bg-amber-100 text-amber-800"
                      }
                    >
                      <Icon className="mr-1 h-3.5 w-3.5" />
                      {getIssueLabel(issue)}
                    </Badge>
                    <span className="font-semibold text-slate-900">
                      {providerName}
                    </span>
                  </div>

                  <div className="flex items-start gap-1.5 text-sm text-slate-600">
                    <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                      {issue.facilityName}
                      {issue.facilityCity && issue.facilityState
                        ? ` • ${issue.facilityCity}, ${issue.facilityState}`
                        : ""}
                    </span>
                  </div>

                  {issue.issueNote && (
                    <div className="rounded-md border border-amber-200 bg-white p-3 text-sm text-slate-700">
                      <span className="font-medium">
                        {isRejected ? "Rejection reason: " : "Provider note: "}
                      </span>
                      {issue.issueNote}
                    </div>
                  )}

                  {!isRejected && canResolve && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={resolvingShiftId === issue.shiftId}
                        >
                          {resolvingShiftId === issue.shiftId ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Check className="mr-2 h-4 w-4" />
                          )}
                          Mark resolved
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            Mark this report as resolved?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            This removes it from Needs attention. The original
                            provider report remains recorded, and the shift will
                            not return to Awaiting submission.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => onResolve(issue.shiftId)}
                          >
                            Mark resolved
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>

                <div className="shrink-0 text-left text-sm sm:text-right">
                  <div className="font-medium text-slate-800">
                    {formatDateInTimezone(
                      issue.scheduledStartsAt,
                      issue.timezoneId,
                    )}
                  </div>
                  <div className="text-slate-600">
                    Scheduled{" "}
                    {formatTimeInTimezone(
                      issue.scheduledStartsAt,
                      issue.timezoneId,
                    )}{" "}
                    –{" "}
                    {formatTimeInTimezone(
                      issue.scheduledEndsAt,
                      issue.timezoneId,
                    )}
                  </div>
                  {issue.submittedStartsAt && issue.submittedEndsAt && (
                    <div className="text-slate-500">
                      Submitted{" "}
                      {formatTimeInTimezone(
                        issue.submittedStartsAt,
                        issue.timezoneId,
                      )}{" "}
                      –{" "}
                      {formatTimeInTimezone(
                        issue.submittedEndsAt,
                        issue.timezoneId,
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
